import React, { useMemo, useEffect, useCallback, useState } from 'react';
import axios from 'axios';
import { ENDPOINT } from 'constants';
import { useSelector, useDispatch } from 'react-redux';
import {
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Alert,
  CircularProgress,
  Button
} from '@mui/material';
import {
  WarningAmber as WarningIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { 
  selectAllCategoriesWithFeatures,
  selectLoading,
  selectCurrentOperation,
  selectError,
  selectFeaturesForCategories,
  fetchCategoriesOptimized
} from 'store/slices/valuePackSlice';
import { FormAutocompleteField, renderCategoryTags } from '../components/FormField';
import { CATEGORIES_FIELDS } from 'config/valuePackFormConfig';
import { useValuePackForm } from 'hooks/useValuePackForm';
import CustomFilterFields from '../components/CustomFilterFields';
import { setRequiredFormFilters } from '../components/requiredFormFilters';
import { getSortMode } from 'layouts/dynamicFilters/filterConfig';

// A value pack can combine at most this many categories (see categoriesSchema)
const MAX_CATEGORIES = 5;

const CategoriesStep = ({ stepIndex = 1, isEdit, validationTriggered = false }) => {
  const { 
    control, 
    getFieldValue, 
    setFieldValue,
    hasFieldError, 
    getFieldError,
    trigger
  } = useValuePackForm(stepIndex, isEdit, validationTriggered);
  
  const dispatch = useDispatch();
  
  const allCategoriesWithFeatures = useSelector(selectAllCategoriesWithFeatures);
  const loading = useSelector(selectLoading);
  const currentOperation = useSelector(selectCurrentOperation);
  const error = useSelector(selectError);
  const accessToken = useSelector((state) => state.auth?.user?.accessToken);

  const selectedCategoryIds = getFieldValue('categories') || [];
  
  // Get features for selected categories
  const selectedCategoryFeatures = useSelector(state => 
    selectFeaturesForCategories(state, selectedCategoryIds)
  );

  // Computed states
  const isCategoriesLoading = loading && ['fetchCategories', 'fetchCategoriesOptimized'].includes(currentOperation);
  const categoriesError = error && ['fetchCategories', 'fetchCategoriesOptimized'].includes(currentOperation) ? error : null;

  const selectedCategories = useMemo(() => {
    if (!Array.isArray(allCategoriesWithFeatures) || !Array.isArray(selectedCategoryIds)) {
      return [];
    }
    
    return allCategoriesWithFeatures.filter(cat => 
      selectedCategoryIds.map(String).includes(String(cat.id))
    );
  }, [allCategoriesWithFeatures, selectedCategoryIds]);

  // Core features of a category: from the category object itself, else the features store
  const getCategoryFeatures = useCallback(
    (category) => {
      if (Array.isArray(category?.features)) return category.features;
      return selectedCategoryFeatures[category?.id]?.features || [];
    },
    [selectedCategoryFeatures]
  );

  // Selected categories that have no feature assigned yet
  const categoriesWithoutFeatures = useMemo(
    () => selectedCategories.filter((category) => getCategoryFeatures(category).length === 0),
    [selectedCategories, getCategoryFeatures]
  );

  const loadCategoriesWithFeatures = useCallback(async () => {
    try {
      await dispatch(fetchCategoriesOptimized({
        limit: 15
      })).unwrap();
    } catch (error) {
      console.error('Failed to load optimized categories:', error);
    }
  }, [dispatch]);

  useEffect(() => {
    if (!isCategoriesLoading && allCategoriesWithFeatures.length === 0 && !categoriesError) {
      loadCategoriesWithFeatures();
    }
  }, [loadCategoriesWithFeatures, isCategoriesLoading, allCategoriesWithFeatures.length, categoriesError]);

  const handleRetryFetch = () => {
    loadCategoriesWithFeatures();
  };

  const handleCategoriesChange = useCallback(async (newValue) => {
    try {
      // Ensure we're passing a mutable array
      const mutableIds = newValue && Array.isArray(newValue) 
        ? Array.from(newValue).map(item => 
            typeof item === 'object' ? item.id : item
          )
        : [];

      // console.log("mutable ids : ", mutableIds);
      
      setFieldValue('categories', mutableIds);
      
      if (validationTriggered) {
        await trigger('categories');
      }
    } catch (error) {
      console.error('Error handling categories change:', error);
    }
  }, [setFieldValue, validationTriggered, trigger]);

  const handleFieldBlur = useCallback(async (fieldName) => {
    if (validationTriggered) {
      await trigger(fieldName);
    }
  }, [validationTriggered, trigger]);

  // Filters from Settings > Dynamic Filters (Value Packs module)
  const [dynamicFilters, setDynamicFilters] = useState([]);
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${ENDPOINT}api/v1/admin/dynamic-filters`, {
        params: { module: 'value_pack' },
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      .then(({ data }) => {
        if (!cancelled) setDynamicFilters(data?.data || []);
      })
      .catch((err) => console.error('Failed to load dynamic filters:', err));
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  const customFilters = useMemo(() => {
    const fields = dynamicFilters.filter(
      (f) => f.source === 'custom' && f.showInForm !== false && f.options?.length
    );

    // Sort filter: its "Ticked packs first" options become checkboxes, a ticked
    // pack is listed first when a shopper picks that sort.
    const sortFilter = dynamicFilters.find((f) => f.source === 'sort');
    const manualSorts = (sortFilter?.options || []).filter((o) => getSortMode(o) === 'manual');
    if (sortFilter && manualSorts.length) {
      fields.push({
        ...sortFilter,
        type: 'checkbox',
        label: 'Show first when sorted by',
        helpText: 'Tick the sort options this value pack should be listed first in on the Value Packs page',
        options: manualSorts,
      });
    }
    return fields;
  }, [dynamicFilters]);

  // Fields set to "Required in the value pack form": checked by the step schema
  useEffect(() => {
    setRequiredFormFilters(
      customFilters.filter((f) => f.source === 'custom' && f.requiredInForm)
    );
  }, [customFilters]);

  const CategoriesPreview = React.memo(({ categories }) => {
    if (!Array.isArray(categories) || categories.length === 0) {
      return (
        <Typography variant="body2" color="textSecondary" textAlign="center" py={4}>
          Select categories to see their features
        </Typography>
      );
    }

    return (
      <Grid container spacing={2}>
        {categories.map((category, index) => {
          const categoryFeatures = getCategoryFeatures(category);
          const hasFeatures = categoryFeatures.length > 0;

          return (
            <Grid item xs={12} md={6} key={category.id || index}>
              <Box
                sx={{
                  height: '100%',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 2,
                }}>
                <Typography variant="subtitle1" fontWeight={600} mb={1}>
                  {category.name}
                  {hasFeatures && (
                    <Typography component="span" variant="caption" color="textSecondary" ml={1}>
                      ({categoryFeatures.length})
                    </Typography>
                  )}
                </Typography>

                {hasFeatures ? (
                  <Box display="flex" flexWrap="wrap" gap={0.75}>
                    {categoryFeatures.map((feature, featureIndex) => (
                      <Chip
                        key={feature.id || featureIndex}
                        size="small"
                        label={feature.name || feature.title || 'Feature'}
                      />
                    ))}
                  </Box>
                ) : (
                  <Box display="flex" alignItems="center" gap={0.75} color="warning.main">
                    <WarningIcon fontSize="small" />
                    <Typography variant="body2" color="inherit">
                      No features assigned
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          );
        })}
      </Grid>
    );
  });

  if (isCategoriesLoading && allCategoriesWithFeatures.length === 0) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="body2" color="textSecondary">
          Loading categories and features...
        </Typography>
      </Box>
    );
  }

  if (categoriesError && allCategoriesWithFeatures.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Alert 
          severity="error" 
          sx={{ width: '100%' }}
          action={
            <Button 
              onClick={handleRetryFetch}
              color="inherit"
              size="small"
              disabled={isCategoriesLoading}
            >
              {isCategoriesLoading ? 'Loading...' : 'Retry'}
            </Button>
          }
        >
          Failed to load categories: {categoriesError}
        </Alert>
      </Box>
    );
  }

  if (allCategoriesWithFeatures.length === 0 && !isCategoriesLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Alert 
          severity="info" 
          sx={{ width: '100%' }}
          action={
            <Button 
              onClick={handleRetryFetch}
              color="inherit"
              size="small"
              disabled={isCategoriesLoading}
            >
              {isCategoriesLoading ? 'Loading...' : 'Load Categories'}
            </Button>
          }
        >
          No categories available. Click to load categories with features.
        </Alert>
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      {/* Categories Selection */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                Select Categories <Typography component="span" color="error">*</Typography>
              </Typography>
            </Box>
            
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Choose at least 2 categories to combine in this value pack. Features are automatically loaded.
            </Typography>
          
            <FormAutocompleteField
              name="categories"
              control={control}
              required={true}
              label={CATEGORIES_FIELDS.categories.label}
              placeholder={CATEGORIES_FIELDS.categories.placeholder}
              helperText={
                getFieldError('categories') ||
                `${selectedCategoryIds.length} of ${MAX_CATEGORIES} categories selected` +
                  (selectedCategoryIds.length >= MAX_CATEGORIES ? ' (maximum reached)' : '')
              }
              options={allCategoriesWithFeatures}
              getOptionLabel={(option) => option?.name || ''}
              // Once the limit is reached, only the already selected options stay
              // clickable (to deselect); the rest cannot be picked.
              getOptionDisabled={(option) =>
                selectedCategoryIds.length >= MAX_CATEGORIES &&
                !selectedCategoryIds.map(String).includes(String(option?.id || option))
              }
              isOptionEqualToValue={(option, value) => {
                const optionId = option?.id || option;
                const valueId = value?.id || value;
                return optionId === valueId;
              }}
              renderTags={(value, getTagProps) => 
                renderCategoryTags(value, getTagProps, allCategoriesWithFeatures)
              }
              // onChange={(event, newValue) => {
              //   const ids = Array.isArray(newValue) 
              //     ? newValue.map(item => (typeof item === 'object' ? item.id : item))
              //     : [];
              //   handleCategoriesChange(ids);
              // }}
              onChange={(event, newValue) => {
                const ids = Array.isArray(newValue) 
                  ? Array.from(newValue).map(item => 
                      typeof item === 'object' ? item.id : item
                    )
                  : [];
                handleCategoriesChange(ids);
              }}
              onBlur={() => handleFieldBlur('categories')}
            />
            
            {/* Show loading indicator while refreshing */}
            {isCategoriesLoading && allCategoriesWithFeatures.length > 0 && (
              <Box display="flex" alignItems="center" mt={1}>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                <Typography variant="caption" color="textSecondary">
                  Refreshing categories and features...
                </Typography>
              </Box>
            )}
            
            {/* Warning for selected categories that have no features assigned */}
            {categoriesWithoutFeatures.length > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                No features assigned to:{' '}
                <strong>{categoriesWithoutFeatures.map((category) => category.name).join(', ')}</strong>
              </Alert>
            )}

            {/* Success indicator for optimized loading */}
            {!isCategoriesLoading && allCategoriesWithFeatures.length > 0 && (
              <Box mt={1}>
                <Typography variant="caption" color="success.main">
                  {allCategoriesWithFeatures.length} categories with features loaded successfully!
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
      
      {/* Target Industries was removed: industries are tagged with a custom filter
          from Settings > Dynamic Filters (shown below), like any other attribute */}

      {/* Categories Preview */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Categories Preview
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Selected categories and their features
            </Typography>
            
            <CategoriesPreview categories={selectedCategories} />
          </CardContent>
        </Card>
      </Grid>

      {/* One card per custom filter set to "Show in value pack form" (Settings > Dynamic Filters) */}
      <CustomFilterFields
        filters={customFilters}
        value={getFieldValue('filterValues')}
        onChange={(next) => setFieldValue('filterValues', next)}
        showErrors={validationTriggered}
      />
    </Grid>
  );
};

export default React.memo(CategoriesStep);
